import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardBody, Badge } from '@repo/ui';
import { Icon } from '@repo/ui';
import { showToast } from '@repo/ui';
import { api } from '@xtnd/web/shared/api';

interface ClioCustomField {
  id: number;
  etag: string;
  field_name: string;
  name: string;
  field_type: 'text' | 'number' | 'date' | 'checkbox' | 'dropdown' | 'textarea';
  field_required: boolean;
  field_displayed: boolean;
  field_display_order: number[];
  parent_type: 'Matter' | 'Contact' | 'Activity';
  created_at: string;
  updated_at: string;
  soft_deleted?: boolean;
  dropdown_options?: string[];
  picklist_options?: Array<{
    id: number;
    option: string;
  }>;
}

interface CustomFieldManagementProps {
  expectedFieldName?: string;
}

export function CustomFieldManagement({ 
  expectedFieldName = 'Enable Juniper Processing' 
}: CustomFieldManagementProps) {
  const queryClient = useQueryClient();
  const [showAllFields, setShowAllFields] = useState(false);

  // Fetch all custom fields
  const { 
    data: customFields = [], 
    isLoading: loadingFields, 
    error: fieldsError,
    refetch: refetchFields 
  } = useQuery<ClioCustomField[]>({
    queryKey: ['clio-all-custom-fields'],
    queryFn: async () => {
      const response = await api.get<{ data: ClioCustomField[] }>('/clio/custom-fields');
      return response.data || [];
    },
    retry: 2,
    retryDelay: 1000,
  });

  // Check if expected field exists
  const expectedField = customFields.find(field => 
    field.name === expectedFieldName || 
    field.field_name === expectedFieldName
  );

  // Create custom field mutation
  const createFieldMutation = useMutation({
    mutationFn: async () => {
      const fieldData = {
        field_name: expectedFieldName, // Note: API expects field_name, not name
        field_type: 'checkbox',
        field_required: false,
        field_displayed: true,
        parent_type: 'Matter',
      };

      const response = await api.post<ClioCustomField>('/clio/custom-fields', fieldData);
      return response;
    },
    onSuccess: (newField) => {
      queryClient.invalidateQueries({ queryKey: ['clio-all-custom-fields'] });
      showToast.success(`Successfully created custom field: ${newField.name}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to create custom field';
      showToast.error(message);
    },
  });

  const handleRefreshFields = () => {
    refetchFields();
  };

  const handleCreateExpectedField = () => {
    createFieldMutation.mutate();
  };

  const formatFieldType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const getFieldBadgeColor = (fieldType: string) => {
    switch (fieldType) {
      case 'checkbox': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'dropdown': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'text': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      case 'number': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'date': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'textarea': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  if (fieldsError) {
    return (
      <Card className="border-red-200 dark:border-red-800">
        <CardHeader>
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200">
            Custom Field Management - Error
          </h3>
        </CardHeader>
        <CardBody>
          <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
            <Icon name="HiExclamationCircle" size="md" />
            <span>Failed to load custom fields. Please try refreshing.</span>
          </div>
          <Button
            variant="outline"
            onClick={handleRefreshFields}
            className="mt-4 border-red-300 text-red-700 hover:bg-red-50"
          >
            <Icon name="HiRefresh" size="sm" className="mr-2" />
            Retry
          </Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="border-xtnd-light">
      <CardHeader>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
            Custom Field Management
          </h3>
          <div className="flex items-center space-x-2">
            {expectedField && (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                <Icon name="HiCheckCircle" size="xs" className="mr-1" />
                Field Exists
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshFields}
              disabled={loadingFields}
              className="border-xtnd-blue text-xtnd-blue hover:bg-xtnd-blue hover:text-white"
            >
              <Icon name="HiRefresh" className={`w-4 h-4 mr-2 ${loadingFields ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        {/* Expected Field Status */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-medium text-xtnd-dark dark:text-xtnd-white">
                Expected Field: {expectedFieldName}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This field is required for Juniper processing
              </p>
            </div>
            {expectedField ? (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                <Icon name="HiCheckCircle" size="xs" className="mr-1" />
                Found
              </Badge>
            ) : (
              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                <Icon name="HiExclamationCircle" size="xs" className="mr-1" />
                Missing
              </Badge>
            )}
          </div>

          {expectedField ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">ID:</span>
                <p className="text-gray-600 dark:text-gray-400">{expectedField.id}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span>
                <p className="text-gray-600 dark:text-gray-400">
                  <Badge className={getFieldBadgeColor(expectedField.field_type)}>
                    {formatFieldType(expectedField.field_type)}
                  </Badge>
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Required:</span>
                <p className="text-gray-600 dark:text-gray-400">
                  {expectedField.field_required ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Created:</span>
                <p className="text-gray-600 dark:text-gray-400">
                  {formatDate(expectedField.created_at)}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                The required custom field "{expectedFieldName}" was not found in your Clio account.
              </p>
              <Button
                variant="primary"
                onClick={handleCreateExpectedField}
                disabled={createFieldMutation.isPending}
                className="bg-xtnd-blue hover:bg-xtnd-blue/90"
              >
                <Icon name="HiPlus" size="sm" className="mr-2" />
                {createFieldMutation.isPending ? 'Creating...' : 'Create Expected Field'}
              </Button>
            </div>
          )}
        </div>

        {/* All Custom Fields */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-xtnd-dark dark:text-xtnd-white">
              All Custom Fields ({customFields.length})
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllFields(!showAllFields)}
              className="text-xtnd-blue hover:bg-xtnd-blue/10"
            >
              <Icon name="HiCog" size="sm" className="mr-2" />
              {showAllFields ? 'Hide' : 'Show'} All Fields
            </Button>
          </div>

          {loadingFields ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                <Icon name="HiRefresh" size="md" className="animate-spin" />
                <span>Loading custom fields...</span>
              </div>
            </div>
          ) : showAllFields && customFields.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {customFields.map((field) => (
                <div
                  key={field.id}
                  className={`border rounded-lg p-3 ${
                    field.name === expectedFieldName || field.field_name === expectedFieldName
                      ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <h5 className="font-medium text-sm text-xtnd-dark dark:text-xtnd-white">
                        {field.name || field.field_name}
                      </h5>
                      <Badge className={getFieldBadgeColor(field.field_type)}>
                        {formatFieldType(field.field_type)}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {field.id}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Parent:</span>
                      <span className="ml-1 text-gray-600 dark:text-gray-400">{field.parent_type}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Required:</span>
                      <span className="ml-1 text-gray-600 dark:text-gray-400">
                        {field.field_required ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Displayed:</span>
                      <span className="ml-1 text-gray-600 dark:text-gray-400">
                        {field.field_displayed ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Created:</span>
                      <span className="ml-1 text-gray-600 dark:text-gray-400">
                        {formatDate(field.created_at)}
                      </span>
                    </div>
                  </div>

                  {field.dropdown_options && field.dropdown_options.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Options:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {field.dropdown_options.map((option, index) => (
                          <Badge key={index} className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 text-xs">
                            {option}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : showAllFields && customFields.length === 0 ? (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
              No custom fields found in your Clio account
            </div>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}
